/**
 * @module  support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { ModalState, StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { Subject } from 'rxjs';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
/**
 * Static Modal Component is a commom component for modals with a static template body. This modal
 * can be triggered through user actions in the DOM or through provider actions.
 */
@Component({
  selector: 'tmo-static-modal',
  templateUrl: './static-modal.component.html',
  styleUrls: ['../../../../../../style-sheets/components/modal.component.scss'],
})
export class StaticModalComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  @ViewChild('mod', { static: true }) public selectModal: ElementRef;
  /**
   * configurable StaticModal input interface
   */
  @Input() public modal: StaticModal;
  /** Event emitted when button is clicked
   * It emits the 'Event' object.
   */
  @Output() public onSelect: EventEmitter<Object>;

  @Input() public verticalAlign?: boolean;
  /**
   * Boolean value to show/hide modal .
   */
  @Input() public displayModal: boolean;
  private accept: string;
  @Output() private onClose: EventEmitter<Object>;
  /**
   * Boolean value to show/hide icon.
   */
  @Input() public showIcon: boolean = true;
  /**
   * string to store logo src
   */
  @Input() public brandLogo: string;
  /**
   * Boolean value to show/hide modal header.
   */
  @Input() public noHeader: boolean;

  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;

  /**
   * constructor function for static modal.
   */
  constructor(activatedRoute: ActivatedRoute, pdlService: PdlService) {
    super(pdlService, activatedRoute);
    this.accept = null;
    this.onClose = new EventEmitter();
    this.onSelect = new EventEmitter();
    this.ngUnsubscribe = new Subject<void>();
  }
  /**
   * this method is executed on load.
   * @returns void
   */
  public ngOnInit(): void {
    super.ngOnInit();
    if (this.modal && this.modal.toggleModalEmitter) {
      this.modal.toggleModalEmitter
        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe((event: ModalState) => {
          if (event.open) {
            this.displayModal = true;
            // this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'modalLoad');
            this.focusModal();
          } else if (event.close) {
            this.displayModal = false;
          } else {
            // TODO: Handle unregistered event
          }
        });
    }
  }
  /**
   * this method is executed when modal gets closed.
   *  @returns void
   */
  public closeDialog(): void {
    this.displayModal = false;
    this.onClose.emit({ accept: this.accept, displayModal: this.displayModal });
  }
  /**
   * This method emits handlerevent which is used to execute
   * the functionality on particular button click.
   * @param event contains information on which button user has clicked
   */
  // tslint:disable no-any
  public handleClickEvent(event: any): void {
    this.accept = event && event.id ? event.id : this.accept;
    this.onSelect.emit(event);
    this.closeDialog();
  }

  /**
   * Destroys all the subscipritions.
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.onClose.unsubscribe();
    this.onSelect.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Force the focus on the model after render.
   */
  public focusModal(): void {
    // @ToDo: Remove Timeout when global stack execution pattern exist
    const timeInterval: number = 0;
    setTimeout(() => {
      this.selectModal.nativeElement.focus();
    }, timeInterval);
  }
}
